import React, { useRef, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Settings, Palette, Volume2 } from 'lucide-react';
import { CosmicWaves } from '@/components/visualizers/CosmicWaves';
import { MirrorSpectrum } from '@/components/visualizers/MirrorSpectrum';
import { FrostyWaves } from '@/components/visualizers/FrostyWaves';
import { DualCore } from '@/components/visualizers/DualCore';
import { AmbientSpectrum } from '@/components/visualizers/AmbientSpectrum';
import { SandyDunes } from '@/components/visualizers/SandyDunes';
import { RandomizeVisualizer, resetRandomizer } from '@/components/visualizers/RandomizeVisualizer';
import { VisualizerLyrics } from '@/components/visualizers/VisualizerLyrics';
import CityWarp from './CityWarp';
import { audioPerformanceLogger } from '@/utils/audioPerformanceLogger';

const visualizers = {
  cosmic: { component: CosmicWaves, name: 'Cosmic Waves' },
  mirror: { component: MirrorSpectrum, name: 'Mirror Spectrum' },
  frosty: { component: FrostyWaves, name: 'Frosty Waves' },
  dual: { component: DualCore, name: 'Dual Core' },
  ambient: { component: AmbientSpectrum, name: 'Ambient Spectrum' },
  sandy: { component: SandyDunes, name: 'Sandy Dunes' },
  citywarp: { component: CityWarp, name: 'City Warp' },
  randomize: { component: RandomizeVisualizer, name: 'Randomize' }
};

// Original default animation when nothing is playing - optimized for performance
function drawDefaultAnimation(ctx, width, height, time) {
  if (!ctx || width <= 0 || height <= 0) return;
  
  // Scale factors based on canvas size for responsive animation
  const minDimension = Math.min(width, height);
  const scale = minDimension / 600; // Base scale on 600px reference
  
  // Clear canvas with dark background
  ctx.fillStyle = 'rgba(0, 0, 0, 0.1)';
  ctx.fillRect(0, 0, width, height);
  
  // Create gradient background (cached for performance)
  const bgGradient = ctx.createRadialGradient(width/2, height/2, 0, width/2, height/2, Math.max(width, height)/2);
  bgGradient.addColorStop(0, 'rgba(59, 130, 246, 0.1)');
  bgGradient.addColorStop(1, 'rgba(0, 0, 0, 0.8)');
  ctx.fillStyle = bgGradient;
  ctx.fillRect(0, 0, width, height);
  
  const centerX = width / 2;
  const centerY = height / 2;
  
  // Scaled animated concentric circles
  for (let i = 0; i < 3; i++) {
    const baseRadius = (50 + i * 60) * scale; // Scale the base radius
    const radius = baseRadius + Math.sin(time * 2 + i) * (15 * scale);
    const alpha = 0.3 - i * 0.08 + Math.sin(time * 3 + i) * 0.1;
    const hue = (time * 40 + i * 80) % 360;
    
    ctx.strokeStyle = `hsla(${hue}, 70%, 60%, ${Math.max(0, alpha)})`;
    ctx.lineWidth = 2 * scale; // Scale line width too
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
    ctx.stroke();
  }
  
  // Scaled floating particles
  for (let i = 0; i < 10; i++) {
    const angle = (i / 10) * Math.PI * 2 + time * 0.4;
    const baseDistance = 120 * scale; // Scale the orbit distance
    const distance = baseDistance + Math.sin(time * 1.5 + i) * (40 * scale);
    const x = centerX + Math.cos(angle) * distance;
    const y = centerY + Math.sin(angle) * distance;
    const size = (2 + Math.sin(time * 3 + i) * 0.8) * scale;
    const hue = (time * 25 + i * 36) % 360;
    const alpha = 0.6 + Math.sin(time * 2.5 + i) * 0.2;
    
    ctx.fillStyle = `hsla(${hue}, 80%, 70%, ${alpha})`;
    ctx.beginPath();
    ctx.arc(x, y, size, 0, Math.PI * 2);
    ctx.fill();
  }
  
  // Scaled central pulsing core
  const coreSize = (15 + Math.sin(time * 3) * 6) * scale;
  const coreGradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, coreSize);
  const coreHue = (time * 50) % 360; // Slower hue rotation
  coreGradient.addColorStop(0, `hsla(${coreHue}, 100%, 80%, 0.8)`);
  coreGradient.addColorStop(0.5, `hsla(${coreHue + 60}, 90%, 70%, 0.5)`);
  coreGradient.addColorStop(1, `hsla(${coreHue + 120}, 80%, 60%, 0)`);
  
  ctx.fillStyle = coreGradient;
  ctx.beginPath();
  ctx.arc(centerX, centerY, coreSize, 0, Math.PI * 2);
  ctx.fill();
  
  // Scaled animated wave
  ctx.strokeStyle = `hsla(${(time * 30) % 360}, 60%, 50%, 0.4)`;
  ctx.lineWidth = 2 * scale;
  ctx.beginPath();
  const step = Math.max(4, 8 * scale); // Scale the step size
  for (let x = 0; x <= width; x += step) {
    const waveAmplitude1 = 40 * scale; // Scale wave amplitude
    const waveAmplitude2 = 20 * scale;
    const y = centerY + Math.sin(x * 0.015 + time * 1.5) * waveAmplitude1 + Math.sin(x * 0.008 + time * 0.8) * waveAmplitude2;
    if (x === 0) {
      ctx.moveTo(x, y);
    } else {
      ctx.lineTo(x, y);
    }
  }
  ctx.stroke();
}

export function VisualizerCore({
  analyserRef,
  dataArrayRef,
  isPlaying,
  currentSong,
  currentTime,
  visualizerType,
  onVisualizerChange,
  width,
  height,
  isStreamerMode,
  lyricsPosition
}) {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const [showControls, setShowControls] = useState(false);
  const [showVisualizerMenu, setShowVisualizerMenu] = useState(false);
  const [lastVisualizerType, setLastVisualizerType] = useState(visualizerType);

  const VisualizerComponent = visualizers[visualizerType]?.component || CosmicWaves;

  // Reset randomizer when switching away from randomize mode
  useEffect(() => {
    if (lastVisualizerType === 'randomize' && visualizerType !== 'randomize') {
      console.log('🎲 Switching away from randomize mode - resetting randomizer');
      resetRandomizer();
    }
    setLastVisualizerType(visualizerType);
  }, [visualizerType, lastVisualizerType]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || width <= 0 || height <= 0) return;

    const ctx = canvas.getContext('2d');
    
    // Set canvas buffer size for DPI; keep CSS size at 100% so the canvas
    // always fills its container even if a measurement was slightly early.
    const dpr = window.devicePixelRatio || 1;
    const displayWidth = width;
    const displayHeight = height;
    
    canvas.width = Math.max(1, Math.floor(displayWidth * dpr));
    canvas.height = Math.max(1, Math.floor(displayHeight * dpr));
    canvas.style.width = '100%';
    canvas.style.height = '100%';
    
    // Reset transform then scale — setTransform avoids compounding on re-runs
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    console.log('🎨 VisualizerCore: Canvas initialized with dimensions:', { 
      width: displayWidth, 
      height: displayHeight, 
      dpr,
      actualCanvasSize: `${canvas.width}x${canvas.height}`
    });
    console.log('🎨 VisualizerCore: isPlaying:', isPlaying, 'analyser:', !!analyserRef.current, 'dataArray:', !!dataArrayRef.current);

    // Performance optimization: Electron-aware frame rate limiting
    let lastFrameTime = 0;
    const isElectron = typeof window !== 'undefined' && window.electronAPI;
    const targetFPS = isElectron ? 35 : 60; // Balanced 35fps for visual smoothness without overloading system
    const frameInterval = 1000 / targetFPS;
    
    // Performance optimization: reduce logging frequency and prevent frame count overflow
    let frameCount = 0;
    const logInterval = isElectron ? 315 : 60; // Every 9 seconds in Electron (35fps * 9s = 315 frames)
    const maxFrameCount = isElectron ? 10000 : 50000; // Reset frame counter to prevent overflow
    
    const animate = (timestamp) => {
      // Skip frame if not enough time has passed (60fps limiting)
      if (timestamp - lastFrameTime < frameInterval) {
        animationRef.current = requestAnimationFrame(animate);
        return;
      }
      lastFrameTime = timestamp;
      
      const time = timestamp * 0.001; // Convert to seconds for smooth animation
      
      // Performance optimization: use willReadFrequently for better canvas performance
      if (!ctx.willReadFrequently) {
        ctx.willReadFrequently = true;
      }
      
      ctx.clearRect(0, 0, width, height);
      
      if (isPlaying && analyserRef.current && dataArrayRef.current) {
        analyserRef.current.getByteFrequencyData(dataArrayRef.current);
        
        // Reduce logging frequency for performance (Electron-aware)
        if (frameCount % logInterval === 0) {
          const dataSample = Array.from(dataArrayRef.current.slice(0, 8));
          console.log('[VISUALIZER] Frame:', { isPlaying, dataSample, platform: isElectron ? 'electron' : 'web', fps: targetFPS });
          
          // Log performance metrics to dashboard
          audioPerformanceLogger.debug('visualizer', 'Frame performance', {
            fps: targetFPS,
            frameCount,
            platform: isElectron ? 'electron' : 'web',
            dataSample,
            hasAudioData: !dataSample.every(v => v === 0)
          });
          
          if (dataSample.every(v => v === 0)) {
            console.warn('[VISUALIZER] All-zero dataArray detected!');
            audioPerformanceLogger.warn('visualizer', 'No audio data received by visualizer', {
              analyserConnected: !!analyserRef.current,
              dataArrayLength: dataArrayRef.current?.length
            });
          }
        }
        
        frameCount++;
        
        // Reset frame counter to prevent overflow and memory issues
        if (frameCount >= maxFrameCount) {
          console.log('[VISUALIZER] Resetting frame counter to prevent overflow');
          frameCount = 0;
        }
        
        try {
          if (VisualizerComponent) {
            VisualizerComponent({
              ctx,
              dataArray: dataArrayRef.current,
              width,
              height,
              isPlaying,
              time // Pass time for optimized animations
            });
          }
        } catch (err) {
          console.error('Visualizer render error:', err);
          audioPerformanceLogger.error('visualizer', 'Visualizer render error', {
            error: err.message,
            stack: err.stack,
            visualizerType,
            width,
            height
          });
          
          ctx.clearRect(0, 0, width, height);
          ctx.fillStyle = '#111';
          ctx.fillRect(0, 0, width, height);
          ctx.fillStyle = '#fff';
          ctx.font = '16px sans-serif';
          ctx.fillText('Visualizer error', 20, 40);
        }
      } else {
        // Show original default animation when not playing (also throttled)
        drawDefaultAnimation(ctx, width, height, time);
      }
      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
      
      // Clear canvas on cleanup to free memory
      if (canvas && ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
    };
  }, [isPlaying, VisualizerComponent, width, height, visualizerType]);

  // Streamer mode styling
  const containerBg = isStreamerMode ? 'bg-black' : 'bg-black/20 backdrop-blur-sm';
  const borderColor = isStreamerMode ? 'border-white/5' : 'border-white/20';

  // Determine lyrics position based on lyricsPosition prop and isStreamerMode
  let lyricsContainerClasses = "absolute pointer-events-none left-6 right-6 z-20"; // Default z-index
  if (lyricsPosition === 'top') {
    lyricsContainerClasses += " top-6";
  } else { // lyricsPosition === 'bottom'
    lyricsContainerClasses += " bottom-8"; // Slightly more padding from bottom in general
  }
  
  // Adjust z-index for streamer mode if lyrics are at the bottom
  if (isStreamerMode && lyricsPosition === 'bottom') {
    lyricsContainerClasses = lyricsContainerClasses.replace('z-20', 'z-30');
  }


  return (
    <div 
      className={`relative w-full h-full ${containerBg} overflow-hidden group`}
      style={{ zIndex: 1, width: '100%', height: '100%' }}
      onMouseEnter={() => setShowControls(true)}
      onMouseLeave={() => {
        setShowControls(false);
        setShowVisualizerMenu(false);
      }}
    >
      {/* Lyrics Display - Positioned based on lyricsPosition prop */}
      <div className={lyricsContainerClasses}>
        <VisualizerLyrics 
          currentSong={currentSong}
          currentTime={currentTime}
          isPlaying={isPlaying}
          isStreamerMode={isStreamerMode} // Pass streamer mode for potential style adjustments
        />
      </div>

      {/* Canvas for visualizer — fills parent; buffer size set in effect */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full"
        style={{ 
          display: 'block',
          width: '100%',
          height: '100%',
          zIndex: 1,
        }}
      />

      {/* Visualizer Controls (show on hover, streamer mode too) */}
      <AnimatePresence>
        {showControls && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="absolute bottom-4 right-4 flex gap-2 z-40 pointer-events-auto"
          >
            <div className="relative">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setShowVisualizerMenu(!showVisualizerMenu)}
                className={`bg-black/40 backdrop-blur-sm text-white hover:bg-black/60 border ${borderColor}`}
              >
                <Palette className="h-4 w-4" />
              </Button>
              <AnimatePresence>
                {showVisualizerMenu && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9, y: 10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.9, y: 10 }}
                    className={`absolute bottom-full right-0 mb-2 bg-black/80 backdrop-blur-sm border ${borderColor} rounded-lg p-2 min-w-48 z-50 pointer-events-auto`}
                  >
                    <div className="space-y-1">
                      {Object.entries(visualizers).map(([key, { name }]) => (
                        <button
                          key={key}
                          onClick={() => {
                            onVisualizerChange(key);
                            setShowVisualizerMenu(false);
                          }}
                          className={`w-full text-left px-3 py-2 rounded text-sm transition-colors ${
                            visualizerType === key
                              ? 'bg-blue-500/30 text-blue-300'
                              : 'text-white/80 hover:bg-white/10'
                          }`}
                        >
                          {name}
                        </button>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Crisp outline for streamer mode */}
      {isStreamerMode && (
        <div className="absolute inset-0 border border-white/10 rounded-lg pointer-events-none" />
      )}
    </div>
  );
}